import React, { useState, useEffect } from 'react';
import { buildApiUrl } from '../config';

const HomeworksBank = ({ onSelectHomework, onClose }) => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedYears, setExpandedYears] = useState({});
  const [expandedSubjects, setExpandedSubjects] = useState({});

  // Year mapping to extract year number from class names
  const extractYearFromClass = (className) => {
    if (!className) return 'Other';
    
    // Handle different class naming patterns
    if (className.includes('PS') || className.includes('Pre-School')) return 'Pre-School';
    if (className.includes('R ') || className.startsWith('R')) return 'Reception';
    
    // Extract year number for Year 1-13
    const yearMatch = className.match(/(\d+)/);
    if (yearMatch) {
      const yearNum = parseInt(yearMatch[1]);
      if (yearNum >= 1 && yearNum <= 13) {
        return `Year ${yearNum}`;
      }
    }
    
    return 'Other';
  };

  useEffect(() => {
    loadHomeworkBank();
  }, []);

  const loadHomeworkBank = async () => {
    try {
      setLoading(true);
      
      // Load all assigned homework from different sources
      const [homeworkResponse, teacherAssignmentsResponse] = await Promise.all([
        fetch(buildApiUrl('/debug-homeworks/')),
        // Add cache-busting query parameter to force fresh data
        fetch(buildApiUrl(`/teacher_assignments.json?t=${Date.now()}`))
      ]);

      let allAssignments = [];

      // Load from debug-homeworks endpoint
      if (homeworkResponse.ok) {
        const homeworkData = await homeworkResponse.json();
        // debug-homeworks returns {homeworks: [...]}
        if (homeworkData.homeworks && Array.isArray(homeworkData.homeworks)) {
          allAssignments = [...allAssignments, ...homeworkData.homeworks];
        }
      }

      // Load from teacher_assignments.json
      if (teacherAssignmentsResponse.ok) {
        const teacherData = await teacherAssignmentsResponse.json();
        if (Array.isArray(teacherData)) {
          allAssignments = [...allAssignments, ...teacherData];
        }
      }

      // Filter out only assigned homework (not drafts) and organize by year/subject
      const assignedHomework = allAssignments.filter(hw => 
        hw.assignedTo && 
        hw.assignedTo.length > 0 && 
        hw.title && 
        hw.subject
      );

      setAssignments(assignedHomework);
    } catch (error) {
      console.error('Error loading homework bank:', error);
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  };

  const organizeByYearAndSubject = () => {
    const organized = {};

    assignments.forEach(homework => {
      // Get all classes this homework is assigned to
      const assignedClasses = Array.isArray(homework.assignedTo) 
        ? homework.assignedTo 
        : [homework.assignedTo];

      assignedClasses.forEach(className => {
        const year = extractYearFromClass(className);
        const subject = homework.subject || 'Other';

        if (!organized[year]) {
          organized[year] = {};
        }
        if (!organized[year][subject]) {
          organized[year][subject] = [];
        }

        // Avoid duplicates
        const exists = organized[year][subject].some(hw => hw.id === homework.id);
        if (!exists) {
          organized[year][subject].push({
            ...homework,
            originalClass: className
          });
        }
      });
    });

    return organized;
  };

  const toggleYear = (year) => {
    setExpandedYears(prev => ({
      ...prev,
      [year]: !prev[year]
    }));
  };

  const toggleSubject = (year, subject) => {
    const key = `${year}-${subject}`;
    setExpandedSubjects(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleLoadHomework = (homework) => {
    if (onSelectHomework) {
      onSelectHomework(homework);
    }
  };

  const handleDeleteHomework = async (homework) => {
    if (!window.confirm(`Are you sure you want to delete "${homework.title}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(buildApiUrl(`/homework-by-id/${homework.id}`), {
        method: 'DELETE',
      });

      if (response.ok) {
        alert('✅ Homework deleted successfully!');
        // Reload the homework bank
        loadHomeworkBank();
      } else {
        const error = await response.json();
        alert(`❌ Failed to delete homework: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting homework:', error);
      alert('❌ Error deleting homework. Please try again.');
    }
  };

  const organizedHomework = organizeByYearAndSubject();
  const years = Object.keys(organizedHomework).sort((a, b) => {
    // Custom sorting for year groups
    const yearOrder = ['Pre-School', 'Reception', 'Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5', 'Year 6', 'Year 7', 'Year 8', 'Year 9', 'Year 10', 'Year 11', 'Year 12', 'Year 13', 'Other'];
    return yearOrder.indexOf(a) - yearOrder.indexOf(b);
  });

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Homework Bank</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>
        <div className="flex justify-center items-center h-64">
          <div className="text-lg text-gray-600">Loading homework bank...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex flex-col overflow-hidden">
      {/* Header with Space Theme */}
      <div className="bg-white/80 backdrop-blur-md shadow-lg border-b border-purple-200/50 p-4">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {onClose && (
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors group"
                  title="Back to Assignments"
                >
                  <svg className="w-6 h-6 text-gray-600 group-hover:text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-500 to-orange-600 flex items-center justify-center shadow-md">
                <span className="text-2xl">🏦</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent">
                  Homework Bank
                </h1>
                <p className="text-gray-600 text-xs mt-0.5">Browse and reuse previous assignments • {assignments.length} missions available</p>
              </div>
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="w-10 h-10 rounded-lg bg-gradient-to-br from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 transition-all duration-300 flex items-center justify-center shadow-md"
              >
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-5xl mx-auto">
        {years.length === 0 ? (
          <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-12 text-center border border-purple-200/50">
            <div className="w-24 h-24 bg-gradient-to-br from-yellow-100 to-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-5xl">🏦</span>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Homework Bank is Empty</h3>
            <p className="text-gray-600">Assignments will appear here after being published to students.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {years.map(year => {
              const subjects = Object.keys(organizedHomework[year]).sort();
              const isYearExpanded = expandedYears[year];

              return (
                <div key={year} className="bg-white/80 backdrop-blur-sm rounded-xl shadow-md overflow-hidden border border-purple-200/50">
                  <button
                    onClick={() => toggleYear(year)}
                    className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 transition-all duration-300"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                        <span className="text-xl">📁</span>
                      </div>
                      <div className="text-left">
                        <span className="text-lg font-bold text-white">{year}</span>
                        <p className="text-blue-100 text-xs mt-0.5">
                          {subjects.length} subject{subjects.length !== 1 ? 's' : ''} • {subjects.reduce((acc, subj) => acc + organizedHomework[year][subj].length, 0)} assignments
                        </p>
                      </div>
                    </div>
                    <svg className={`w-5 h-5 text-white transform transition-transform duration-300 ${isYearExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {isYearExpanded && (
                    <div className="p-3 space-y-2 bg-gradient-to-br from-purple-50/50 to-pink-50/50">
                      {subjects.map(subject => {
                        const homeworkList = organizedHomework[year][subject];
                        const subjectKey = `${year}-${subject}`;
                        const isSubjectExpanded = expandedSubjects[subjectKey];

                        return (
                          <div key={subject} className="bg-white rounded-lg shadow-sm overflow-hidden border border-indigo-200">
                            <button
                              onClick={() => toggleSubject(year, subject)}
                              className="w-full flex items-center justify-between p-3 bg-gradient-to-r from-indigo-400 to-purple-400 hover:from-indigo-500 hover:to-purple-500 transition-all duration-300"
                            >
                              <div className="flex items-center space-x-2.5">
                                <span className="text-lg">📚</span>
                                <div className="text-left">
                                  <span className="font-semibold text-white text-sm">{subject}</span>
                                  <p className="text-indigo-100 text-xs">
                                    {homeworkList.length} assignment{homeworkList.length !== 1 ? 's' : ''}
                                  </p>
                                </div>
                              </div>
                              <svg className={`w-4 h-4 text-white transform transition-transform duration-300 ${isSubjectExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>

                            {isSubjectExpanded && (
                              <div className="overflow-x-auto">
                                <table className="w-full">
                                  <thead className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-indigo-200">
                                    <tr>
                                      <th className="px-3 py-2 text-left text-xs font-bold text-indigo-900">Title</th>
                                      <th className="px-3 py-2 text-left text-xs font-bold text-indigo-900">Class</th>
                                      <th className="px-3 py-2 text-left text-xs font-bold text-indigo-900">Created</th>
                                      <th className="px-3 py-2 text-center text-xs font-bold text-indigo-900">Questions</th>
                                      <th className="px-3 py-2 text-center text-xs font-bold text-indigo-900">Action</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-indigo-100">
                                    {homeworkList.map((homework, index) => (
                                      <tr 
                                        key={`${homework.id}-${index}`}
                                        className="hover:bg-indigo-50/50 transition-colors group"
                                      >
                                        <td className="px-3 py-2">
                                          <div className="flex items-center space-x-2">
                                            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center flex-shrink-0 shadow-sm">
                                              <span className="text-white text-sm">📝</span>
                                            </div>
                                            <div>
                                              <div className="font-medium text-gray-900 text-sm">{homework.title}</div>
                                              {homework.createdBy && (
                                                <div className="text-xs text-gray-600">By: {homework.createdBy}</div>
                                              )}
                                            </div>
                                          </div>
                                        </td>
                                        <td className="px-3 py-2">
                                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                                            {homework.originalClass}
                                          </span>
                                        </td>
                                        <td className="px-3 py-2">
                                          <div className="flex items-center space-x-1.5">
                                            <span className="text-base">📅</span>
                                            <span className="text-xs font-medium text-gray-700">
                                              {new Date(homework.createdAt).toLocaleDateString()}
                                            </span>
                                          </div>
                                        </td>
                                        <td className="px-3 py-2 text-center">
                                          <div className="inline-flex items-center space-x-1.5 px-2 py-1 bg-purple-50 rounded border border-purple-200">
                                            <span className="text-sm">❓</span>
                                            <span className="font-bold text-purple-700 text-sm">
                                              {homework.questions?.length || 0}
                                            </span>
                                          </div>
                                        </td>
                                        <td className="px-3 py-2 text-center">
                                          <div className="flex items-center justify-center space-x-2">
                                            <button
                                              onClick={() => handleLoadHomework(homework)}
                                              className="px-3 py-1.5 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-lg transition-all duration-300 shadow-sm hover:shadow-md font-medium group-hover:scale-105 transform text-xs"
                                            >
                                              🚀 Load
                                            </button>
                                            <button
                                              onClick={() => handleDeleteHomework(homework)}
                                              className="px-2 py-1.5 bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white rounded-lg transition-all duration-300 shadow-sm hover:shadow-md font-medium group-hover:scale-105 transform text-xs"
                                              title="Delete homework"
                                            >
                                              🗑️
                                            </button>
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default HomeworksBank;
